"use client"

import { useCallback, useEffect, useState } from "react"

/**
 * Lightweight client-side cart — persists to localStorage.
 *
 * We keep the cart in the browser until checkout to avoid a Payload schema
 * push for a transient structure. On checkout the cart is serialised into
 * an Order document (server-side).
 */

export type CartItem = {
  id: string
  slug: string
  name: string
  brand?: string
  image?: string
  unitPrice: number
  quantity: number
}

const STORAGE_KEY = "adis95.cart.v1"

function read(): CartItem[] {
  if (typeof window === "undefined") return []
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const data = JSON.parse(raw)
    if (!Array.isArray(data)) return []
    return data
  } catch {
    return []
  }
}

function write(items: CartItem[]) {
  if (typeof window === "undefined") return
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  window.dispatchEvent(new CustomEvent("adis95:cart-updated"))
}

export function useCart() {
  const [items, setItems] = useState<CartItem[]>([])
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setItems(read())
    setHydrated(true)
    const onUpdate = () => setItems(read())
    window.addEventListener("adis95:cart-updated", onUpdate)
    window.addEventListener("storage", onUpdate)
    return () => {
      window.removeEventListener("adis95:cart-updated", onUpdate)
      window.removeEventListener("storage", onUpdate)
    }
  }, [])

  const add = useCallback((item: Omit<CartItem, "quantity"> & { quantity?: number }) => {
    const next = [...read()]
    const existing = next.find((i) => i.id === item.id)
    if (existing) {
      existing.quantity += item.quantity ?? 1
    } else {
      next.push({ ...item, quantity: item.quantity ?? 1 })
    }
    write(next)
  }, [])

  const update = useCallback((id: string, quantity: number) => {
    const next = read()
      .map((i) => (i.id === id ? { ...i, quantity } : i))
      .filter((i) => i.quantity > 0)
    write(next)
  }, [])

  const remove = useCallback((id: string) => {
    write(read().filter((i) => i.id !== id))
  }, [])

  const clear = useCallback(() => {
    write([])
  }, [])

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0)
  const subtotal = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0)

  return { items, hydrated, add, update, remove, clear, totalItems, subtotal }
}
